import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Modal, Button, Space } from "antd";
import { CheckCircleFilled, CloseCircleFilled, FormatPainterOutlined, CompressOutlined } from "@ant-design/icons";

export default function JsonEditorModal({ visible, value, onSave, onCancel, title }) {
  const [editValue, setEditValue] = useState("");
  const textareaRef = useRef(null);

  useEffect(() => {
    if (visible) {
      try {
        const parsed = JSON.parse(value);
        setEditValue(JSON.stringify(parsed, null, 2));
      } catch {
        setEditValue(value || "");
      }
      // Auto-focus the textarea after modal opens
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [visible, value]);

  const validation = useMemo(() => {
    try {
      JSON.parse(editValue);
      return { isValid: true, error: null };
    } catch (e) {
      return { isValid: false, error: e.message };
    }
  }, [editValue]);

  const lineCount = editValue.split("\n").length;

  // Handle Tab key for indentation inside the editor
  const handleKeyDown = useCallback((e) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    if (e.key === "Tab") {
      e.preventDefault();
      const { selectionStart, selectionEnd } = textarea;
      const before = editValue.slice(0, selectionStart);
      const selected = editValue.slice(selectionStart, selectionEnd);
      const after = editValue.slice(selectionEnd);

      if (selected.includes("\n")) {
        // Multi-line: indent/dedent all selected lines
        const lines = selected.split("\n");
        const modified = e.shiftKey
          ? lines.map((l) => (l.startsWith("  ") ? l.slice(2) : l))
          : lines.map((l) => "  " + l);
        const newText = before + modified.join("\n") + after;
        setEditValue(newText);
        // Restore selection
        setTimeout(() => {
          textarea.selectionStart = selectionStart;
          textarea.selectionEnd = selectionStart + modified.join("\n").length;
        }, 0);
      } else if (e.shiftKey) {
        // Shift+Tab: remove 2 spaces before cursor
        if (before.endsWith("  ")) {
          const newText = before.slice(0, -2) + after;
          setEditValue(newText);
          setTimeout(() => {
            textarea.selectionStart = textarea.selectionEnd = selectionStart - 2;
          }, 0);
        }
      } else {
        // Insert 2 spaces at cursor
        const newText = before + "  " + after;
        setEditValue(newText);
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = selectionStart + 2;
        }, 0);
      }
    }

    // Enter key: auto-indent to match previous line
    if (e.key === "Enter") {
      e.preventDefault();
      const { selectionStart } = textarea;
      const before = editValue.slice(0, selectionStart);
      const after = editValue.slice(selectionStart);
      // Get indent of current line
      const currentLine = before.split("\n").pop();
      const indent = currentLine.match(/^(\s*)/)[1];
      // Check if we're after { or [ — add extra indent
      const charBefore = before.trimEnd().slice(-1);
      const charAfter = after.trimStart()[0];
      let newText;
      let cursorOffset;

      if ((charBefore === "{" || charBefore === "[") && (charAfter === "}" || charAfter === "]")) {
        // Between brackets — add indented line + closing bracket line
        newText = before + "\n" + indent + "  " + "\n" + indent + after;
        cursorOffset = selectionStart + 1 + indent.length + 2;
      } else if (charBefore === "{" || charBefore === "[" || charBefore === ",") {
        newText = before + "\n" + indent + "  " + after;
        cursorOffset = selectionStart + 1 + indent.length + 2;
      } else {
        newText = before + "\n" + indent + after;
        cursorOffset = selectionStart + 1 + indent.length;
      }

      setEditValue(newText);
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = cursorOffset;
      }, 0);
    }
  }, [editValue]);

  const handleFormat = () => {
    try {
      const parsed = JSON.parse(editValue);
      setEditValue(JSON.stringify(parsed, null, 2));
    } catch {}
  };

  const handleCompact = () => {
    try {
      const parsed = JSON.parse(editValue);
      setEditValue(JSON.stringify(parsed));
    } catch {}
  };

  // Save — compress if valid JSON, otherwise save as plain text
  const handleSave = () => {
    if (validation.isValid) {
      try {
        const parsed = JSON.parse(editValue);
        onSave(JSON.stringify(parsed));
      } catch {
        onSave(editValue);
      }
    } else {
      // Allow saving plain text too
      onSave(editValue);
    }
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2.5">
          <span className="text-[15px] font-semibold">Edit Value</span>
          {title && <span className="modal-name-chip">{title}</span>}
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={720}
      destroyOnClose
      footer={
        <div className="flex justify-between items-center py-1">
          <Space size={8}>
            <Button
              onClick={handleFormat}
              disabled={!validation.isValid}
              size="small"
              icon={<FormatPainterOutlined />}
            >
              Pretty Print
            </Button>
            <Button
              onClick={handleCompact}
              disabled={!validation.isValid}
              size="small"
              icon={<CompressOutlined />}
            >
              Minify
            </Button>
            <span className="text-[11px] text-slate-400 ml-1">
              {lineCount} lines
            </span>
          </Space>
          <Space size={8}>
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="primary" onClick={handleSave} disabled={!editValue.trim()}>
              Save
            </Button>
          </Space>
        </div>
      }
    >
      <div className="json-editor-wrapper">
        <div className="json-editor-gutter" aria-hidden="true">
          {editValue.split("\n").map((_, i) => (
            <div key={i} className="json-editor-line-num">{i + 1}</div>
          ))}
        </div>
        <textarea
          ref={textareaRef}
          className="json-editor-code"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
        />
      </div>
      <div className={`json-validation ${validation.isValid ? "valid" : "invalid"}`}>
        {validation.isValid ? (
          <>
            <CheckCircleFilled />
            <span>Valid JSON</span>
          </>
        ) : (
          <>
            <CloseCircleFilled />
            <span>{validation.error}</span>
          </>
        )}
      </div>
    </Modal>
  );
}
